import {FrameGridView} from "../frameGrid/FrameGridScene";
import {FrameBlockItem} from "../frameGrid/FrameBlockItem";
import {StickMaterial, stockSticks} from "../frameGrid/StickMaterial";
import {geo, Geo} from "../frameGrid/Joints";
import {findPerimeterStick, findStickById, getStickProfile} from "../frameGrid/BlockOps";
import {itemProfile, MdFrameStick} from "../../model/frame/MdFrameBlock";
import {MdFrameSpec, primaryDoorsItem} from "../../model/frame/MdFrameSpec";
import {inches} from "../../const";
import {CutPlanEnd, CutPlanFace, CutPlanStick} from "../../../../../../api/ElevationTool";
import {ElevationApp} from "../../ElevationApp";
import {getSwingSide} from "../frameProfile/Helpers";

// alignment slop when matching stick ends and faces.
const WIGGLE = inches / 16;

// what a stick face meets in a cell. A void and the threshold are not infill (both are
// filtered out of the faces' cells; this keeps the type honest).
function interfaceKind(i: FrameBlockItem): CutPlanFace["interfaces"][number]["kind"] {
    const k = i.model.kind;
    return k === "void" || k === "threshold" ? "wall" : k;
}

type FaceSide = CutPlanFace["side"];
type EndSide = CutPlanEnd["side"];

type Piece = {
    mat: StickMaterial;
    g: Geo;
    label: string;
    faces: {side: FaceSide; items: FrameBlockItem[]}[];
};

// the cut plan of the laid-out frame grid: every visible stick with a letter, the profile and
// infill on each face, how each end is cut, and the angled-mullion, hinge and strike details.
// Returns the sticks in label order plus the material each label belongs to, so the caller
// can draw the letters.
export function computeCutPlan(view: FrameGridView, elev: ElevationApp): {sticks: CutPlanStick[]; marks: {mat: StickMaterial; label: string}[]} {
    const spec = elev.frameSpec;
    const doorJambs = doorJambDetails(elev);
    // a void is outside the frame, so a face against it faces the wall. The threshold is
    // the door's sill, not an infill.
    const items = (view.grid.getItems() as FrameBlockItem[]).filter(i => !!i.layoutRect && i.model.kind !== "void" && i.model.kind !== "threshold");

    // an air gap is open space between two cells, not a piece to cut.
    const pieces: Piece[] = stockSticks(view.sticks)
        .filter(s => !s.destroyed && s.graphic.visible && s.length.get() > 0)
        .map(mat => {
            const g = geo(mat);
            return {mat, g, label: "", faces: faceSides(g).map(side => ({side, items: touchingItems(g, side, items)}))};
        });

    sortForLabels(pieces, spec).forEach((p, i) => p.label = renderLabel(i));

    const byMat = new Map(pieces.map(p => [p.mat, p]));

    // the stick each outline corner end meets. The corner's terminations say how that end is cut.
    const cornerPartner = new Map<StickMaterial, {A?: StickMaterial; B?: StickMaterial}>();
    for(const c of view.corners) {
        cornerPartner.set(c.h.mat, {...cornerPartner.get(c.h.mat), [c.h.end]: c.v.mat});
        cornerPartner.set(c.v.mat, {...cornerPartner.get(c.v.mat), [c.v.end]: c.h.mat});
    }

    const notches = new Map<Piece, {label: string; center: number}[]>();
    const ends = new Map<Piece, CutPlanEnd[]>();

    for(const p of pieces) {
        const out: CutPlanEnd[] = [];
        for(const end of ["A", "B"] as const) {
            const term = p.mat.termination(end);
            const corner = cornerPartner.get(p.mat)?.[end];
            const partner = corner ? byMat.get(corner) : abuttingPiece(p, end, pieces);

            let contour: CutPlanEnd["contour"] = partner ? "cap" : "square";
            let add = partner ? partner.g.crossMax - partner.g.crossMin : 0;
            if(term.kind === "miter" || term.kind === "extend") {
                contour = term.kind;
                add = term.otherThickness.get();
            } else if(partner) {
                // a cap runs to the midpoint of the stick it meets.
                add = add / 2;
            }
            // an end cut to a sloped stick's faces runs to its long point; a cap there
            // still takes the midpoint allowance.
            if(term.corners) {
                add = Math.max(term.corners.left, term.corners.right) + (contour === "cap" && partner ? (partner.g.crossMax - partner.g.crossMin) / 2 : 0);
            }

            out.push({side: endSide(p.g, end), contour, otherLabel: partner?.label, addIn: add * p.mat.slopeScale() / inches, angleDeg: endAngleDeg(p.mat, end)});

            // a stick that caps into the span of another notches that stick.
            if(partner && !corner && contour === "cap") {
                const list = notches.get(partner) ?? [];
                list.push({label: p.label, center: p.g.crossCenter});
                notches.set(partner, list);
            }
        }
        ends.set(p, out);
    }

    const sticks = pieces.map(p => {
        const pEnds = ends.get(p)!;
        const modelStick = findStickById(spec, p.mat.stickId);
        const jambSide = doorJambSide(p, spec);
        // a sloped stick's lengths run along its slope.
        const scale = p.mat.slopeScale();
        const nominal = (p.g.alongMax - p.g.alongMin) * scale;
        const undercut = p.mat.undercutAmount();
        const addA = pEnds[0].addIn * inches;
        const slopeDeg = Math.acos(1 / scale) * 180 / Math.PI;

        // a hinge or strike jamb's offsets are from the door's top; the cut piece reports them
        // from its A end, like a notch: past the end allowance, and from the piece's own top
        // when it starts lower (split beside a step).
        const doorTop = jambSide ? doorTopOf(p, jambSide, spec) : null;
        const fromPieceEnd = (y: number) => ((y - (p.g.alongMin - (doorTop ?? p.g.alongMin))) * scale + addA) / inches;
        const onPiece = (valueIn: number) => valueIn * inches >= addA && valueIn * inches <= addA + nominal;

        const sided = p.faces.filter(f => f.items.length > 0).length >= 2 ? "two" : "one";

        const stick: CutPlanStick = {
            label: p.label,
            orientation: p.mat.orientation,
            role: stickRole(p, spec),
            sided,
            faces: p.faces.map(f => ({
                side: f.side,
                profiles: faceProfiles(p, f, spec),
                interfaces: f.items.length === 0
                    ? [{kind: "wall"}]
                    : f.items.map(i => ({kind: interfaceKind(i), mark: i.model.kind === "lite" ? i.model.mark : undefined})),
            })),
            nominalIn: nominal / inches,
            lengthIn: (addA + nominal + pEnds[1].addIn * inches - undercut) / inches,
            thicknessIn: p.mat.thickness.get() / inches,
            undercutIn: undercut / inches,
            slopeDeg: slopeDeg > 0.01 ? Math.round(slopeDeg * 10) / 10 : undefined,
            ends: pEnds,
            notches: (notches.get(p) ?? [])
                .sort((a, b) => a.center - b.center)
                .map(n => ({label: n.label, positionIn: ((n.center - p.g.alongMin) * scale + addA) / inches})),
        };

        const angled = modelStick ? angledDetail(modelStick, spec) : undefined;
        if(angled) stick.angled = angled;

        if(jambSide && doorJambs.hingeSides.has(jambSide)) {
            stick.hinges = doorJambs.hinges
                .map(h => ({topIn: fromPieceEnd(h.top), heightIn: h.height / inches}))
                .filter(h => onPiece(h.topIn));
        }
        if(jambSide && doorJambs.strikeSide === jambSide) {
            stick.strikes = doorJambs.strikes
                .map(s => ({kind: s.kind, centerIn: fromPieceEnd(s.y)}))
                .filter(s => onPiece(s.centerIn));
        }

        return stick;
    });

    return {sticks, marks: pieces.map(p => ({mat: p.mat, label: p.label}))};
}

// the angle of an end's cut off square to the stick's own axis (along its slope), in
// degrees, rounded to a tenth: 0 for a square cut, 45 for a miter between two straight
// sticks of one thickness.
function endAngleDeg(mat: StickMaterial, end: "A" | "B"): number {
    const cut = mat.endCut(end);
    const len = mat.length.get();
    // the stick's axis, and the cut line, in (along, cross)
    const axis = {along: len, cross: mat.slope.b - mat.slope.a};
    const line = {along: cut.right.along - cut.left.along, cross: cut.right.cross - cut.left.cross};
    const axisLen = Math.hypot(axis.along, axis.cross);
    const lineLen = Math.hypot(line.along, line.cross);
    if(axisLen === 0 || lineLen === 0) return 0;
    const cos = Math.abs(axis.along * line.along + axis.cross * line.cross) / (axisLen * lineLen);
    const offSquare = Math.asin(Math.min(1, cos)) * 180 / Math.PI;
    return Math.round(offSquare * 10) / 10;
}

// an angled mullion's plan angle and face sizes. The inside face (the side the angle is
// under 180 on) is the jamb face; the outside face adds the run past the hinge to where
// the two halves' outside faces meet: jambDepth / tan(half the interior angle).
function angledDetail(stick: MdFrameStick, spec: MdFrameSpec): CutPlanStick["angled"] {
    if(!stick.angle) return undefined;
    const deg = stick.angle.get();
    const interior = deg < 180 ? deg : 360 - deg;
    if(interior >= 180 - 1e-6 || interior <= 0) return undefined;

    const face = spec.jambFace.get();
    const extension = spec.jambDepth.get() / Math.tan(interior / 2 * Math.PI / 180);
    return {deg, insideFaceIn: face / inches, outsideFaceIn: (face + extension) / inches};
}

// which side of the door a vertical piece is the jamb of: it faces the primary opening's door
// cell on its right (the door's left jamb) or on its left. null for every other piece — a
// secondary door slot's jambs carry that opening's preps, not this one's.
function doorJambSide(p: Piece, spec: MdFrameSpec): "left" | "right" | null {
    if(p.g.orientation !== "vertical") return null;
    const primary = primaryDoorsItem(spec);
    const facesDoor = (side: FaceSide) => !!p.faces.find(f => f.side === side)?.items.some(i => i.model === primary);
    if(facesDoor("right")) return "left";
    if(facesDoor("left")) return "right";
    return null;
}

// the top of the door cell a jamb piece faces, in layout units.
function doorTopOf(p: Piece, jambSide: "left" | "right", spec: MdFrameSpec): number | null {
    const primary = primaryDoorsItem(spec);
    const face = p.faces.find(f => f.side === (jambSide === "left" ? "right" : "left"));
    const door = face?.items.find(i => i.model === primary);
    return door?.layoutRect ? door.layoutRect.y : null;
}

// the hinge and strike details the door's jambs carry: which jambs hinge (both on a pair),
// the hinges (top of hinge from the top of the door, and height), which jamb takes the
// strikes (a single door's lock side) and the strike centres. A door with no hinge swing
// (pocket, bypass, ...) has none.
function doorJambDetails(elev: ElevationApp): {
    hingeSides: Set<"left" | "right">;
    hinges: {top: number; height: number}[];
    strikeSide: "left" | "right" | null;
    strikes: {kind: "lock" | "deadbolt"; y: number}[];
} {
    const none = {hingeSides: new Set<"left" | "right">(), hinges: [], strikeSide: null, strikes: []};

    let swing: ReturnType<typeof getSwingSide>;
    try {
        swing = getSwingSide(elev, true);
    } catch {
        return none;
    }

    const hingeModel = elev.hardware.hinges;
    const hinges = hingeModel.list.map((h, i) => {
        const height = hingeModel.hingeHeightAt(i);
        return {top: h.offsetTop.get() - height / 2, height};
    });

    if(elev.frameSpec.isPair) {
        return {hingeSides: new Set<"left" | "right">(["left", "right"]), hinges, strikeSide: null, strikes: []};
    }

    return {
        hingeSides: new Set<"left" | "right">([swing.side]),
        hinges,
        strikeSide: swing.side === "left" ? "right" : "left",
        strikes: elev.hardware.strikeCenters(),
    };
}

// a 0-based index as a stick letter: A, B, … Z, AA, AB, …
function renderLabel(i: number): string {
    let label = "";
    for(let n = i; n >= 0; n = Math.floor(n / 26) - 1) {
        label = String.fromCharCode(65 + n % 26) + label;
    }
    return label;
}

function faceSides(g: Geo): FaceSide[] {
    return g.orientation === "vertical" ? ["left", "right"] : ["top", "bottom"];
}

function endSide(g: Geo, end: "A" | "B"): EndSide {
    if(g.orientation === "vertical") return end === "A" ? "top" : "bottom";
    return end === "A" ? "left" : "right";
}

// the cells whose laid-out rect sits against the given face, in order along the stick. The match
// allows less than the stick's own thickness, so a cell on the far side never counts.
function touchingItems(g: Geo, side: FaceSide, items: FrameBlockItem[]): FrameBlockItem[] {
    const tol = Math.max(WIGGLE, (g.crossMax - g.crossMin) * 0.75);

    const out = items.filter(i => {
        const r = i.layoutRect!;
        const vertical = g.orientation === "vertical";
        const alongMin = vertical ? r.y : r.x;
        const alongMax = vertical ? r.y + r.height : r.x + r.width;
        if(Math.min(alongMax, g.alongMax) - Math.max(alongMin, g.alongMin) <= WIGGLE) return false;

        switch(side) {
            case "left": return Math.abs(r.x + r.width - g.crossMin) < tol;
            case "right": return Math.abs(r.x - g.crossMax) < tol;
            case "top": return Math.abs(r.y + r.height - g.crossMin) < tol;
            case "bottom": return Math.abs(r.y - g.crossMax) < tol;
        }
        return false;
    });

    return out.sort((a, b) => g.orientation === "vertical"
        ? a.layoutRect!.y - b.layoutRect!.y
        : a.layoutRect!.x - b.layoutRect!.x);
}

// the perpendicular stick this end butts against (a tee or a cross), or undefined for a free end.
function abuttingPiece(p: Piece, end: "A" | "B", pieces: Piece[]): Piece | undefined {
    const at = end === "A" ? p.g.alongMin : p.g.alongMax;

    return pieces.find(o => {
        if(o === p || o.g.orientation === p.g.orientation) return false;
        if(p.g.crossCenter < o.g.alongMin - WIGGLE || p.g.crossCenter > o.g.alongMax + WIGGLE) return false;
        return end === "A" ? Math.abs(at - o.g.crossMax) < WIGGLE : Math.abs(at - o.g.crossMin) < WIGGLE;
    });
}

const perimeterSideOrder = ["left", "top", "right", "bottom"];

// perimeter first (left jamb, head, right jamb, then the bottom pieces left to right), then the
// interior sticks top to bottom, left to right.
function sortForLabels(pieces: Piece[], spec: MdFrameSpec): Piece[] {
    const rank = (p: Piece) => {
        const peri = findPerimeterStick(spec.frameBlocks, p.mat.stickId);
        return peri ? perimeterSideOrder.indexOf(peri.side) : perimeterSideOrder.length;
    };

    return [...pieces].sort((a, b) => {
        const r = rank(a) - rank(b);
        if(r !== 0) return r;
        if(a.mat.positionY !== b.mat.positionY) return a.mat.positionY - b.mat.positionY;
        return a.mat.positionX - b.mat.positionX;
    });
}

function stickRole(p: Piece, spec: MdFrameSpec): CutPlanStick["role"] {
    const peri = findPerimeterStick(spec.frameBlocks, p.mat.stickId);
    if(peri) {
        switch(peri.side) {
            case "left":
            case "right": return "jamb";
            case "top": return "head";
            case "bottom":
                if(peri.role === "threshold") return "threshold";
                if(peri.role === "single") return "sill";
                return "bottomRun";
        }
    }

    const facing = p.faces.filter(f => f.items.length > 0);
    if(facing.length >= 2) return "mullion";

    // an edge stick of a nested block: one side faces a cell, the other the wall.
    if(p.g.orientation === "vertical") return "jamb";
    return facing[0]?.side === "top" ? "sill" : "head";
}

// the distinct profiles along a face: a vertical stick's per-face override, else each touching
// cell's profile. A face against the wall has none.
function faceProfiles(p: Piece, face: Piece["faces"][number], spec: MdFrameSpec): string[] {
    if(face.items.length === 0) return [];

    if(face.side === "left" || face.side === "right") {
        const override = getStickProfile(spec, p.mat.stickId, face.side);
        if(override) return [override];
    }

    return Array.from(new Set(face.items.map(i => itemProfile(spec, i.model))));
}
